import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const guideLinks = [
  { label: 'Shopping Guide', to: '/sugargoo-spreadsheet-shopping-guide' },
  { label: 'Sugargoo Review', to: '/sugargoo-review-is-sugargoo-legit' },
  { label: 'Shipping Cost Guide', to: '/sugargoo-shipping-cost-guide' },
  { label: 'Coupon & Discounts', to: '/sugargoo-coupon-discount-guide' },
  { label: 'Warehouse Guide', to: '/sugargoo-warehouse-guide' },
  { label: 'Finds & Haul Guide', to: '/sugargoo-finds-haul-guide' },
  { label: 'Beginner Mistakes', to: '/sugargoo-beginner-mistakes' },
];

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setGuideOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-background-50/90 backdrop-blur-md border-b border-background-200/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <span className="text-lg md:text-xl font-heading font-semibold text-foreground-900 tracking-tight whitespace-nowrap">
              Sugargoo Spreadsheet
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {/* Home Button */}
            <Link
              to="/"
              className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            {/* Shopping Guide Dropdown */}
            <div ref={guideRef} className="relative">
              <button
                onClick={() => { setGuideOpen(!guideOpen); setDropdownOpen(false); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>Shopping Guide</span>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>

              {guideOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/60 shadow-lg overflow-hidden">
                  {guideLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setGuideOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setGuideOpen(false); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <span>Shopping Categories</span>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/60 shadow-lg overflow-hidden max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                    >
                      <img
                        src={cat.imageSrc}
                        alt={cat.name}
                        className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                      />
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/60 py-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors duration-150"
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500"><i className="ri-home-4-line"></i></span>
              Home
            </Link>

            {/* Mobile Guides Accordion */}
            <button
              onClick={() => { setMobileGuidesOpen(!mobileGuidesOpen); setMobileCategoriesOpen(false); }}
              className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-500"><i className="ri-book-open-line"></i></span>
                Shopping Guide
              </span>
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuidesOpen ? 'rotate-180' : ''}`}></i>
              </span>
            </button>
            {mobileGuidesOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/60 space-y-1">
                {guideLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Mobile Categories Accordion */}
            <button
              onClick={() => { setMobileCategoriesOpen(!mobileCategoriesOpen); setMobileGuidesOpen(false); }}
              className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium text-accent-600 hover:bg-accent-50 transition-colors duration-150 cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-accent-500"><i className="ri-grid-line"></i></span>
                Shopping Categories
              </span>
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCategoriesOpen ? 'rotate-180' : ''}`}></i>
              </span>
            </button>
            {mobileCategoriesOpen && (
              <div className="ml-4 pl-4 border-l-2 border-accent-200/60 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150"
                  >
                    <img
                      src={cat.imageSrc}
                      alt={cat.name}
                      className="w-7 h-7 rounded object-cover flex-shrink-0"
                    />
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}